import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Button,
  Col,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  Row,
  Spinner,
  Table,
  UncontrolledTooltip,
} from "reactstrap";
import { toast } from "react-toastify";
import { submitAIFadvReportClientsFilterData } from "model/AIFadvReportModel";
import {
  getClientRegisterFadvDetails,
  updateClientRegisterAiFadvDetails,
} from "model/ClientRegisterModel";
import { usePermissionContext } from "libs/context/PermissionContext";

const extractClientRows = (response = {}) => {
  const responseData = response?.data ?? response ?? {};
  const nestedData =
    responseData?.data &&
    typeof responseData.data === "object" &&
    !Array.isArray(responseData.data)
      ? responseData.data
      : responseData;

  if (Array.isArray(nestedData?.rows)) return nestedData.rows;
  if (Array.isArray(nestedData?.records)) return nestedData.records;
  if (Array.isArray(nestedData?.clients)) return nestedData.clients;
  if (Array.isArray(nestedData?.data)) return nestedData.data;
  if (Array.isArray(responseData?.rows)) return responseData.rows;
  if (Array.isArray(responseData?.records)) return responseData.records;
  if (Array.isArray(responseData?.data)) return responseData.data;
  if (Array.isArray(responseData)) return responseData;
  return [];
};

const mapClientRows = (rows = []) =>
  (Array.isArray(rows) ? rows : []).map((item = {}, index) => ({
    id: item?.clientId ?? item?.id ?? item?._id ?? `client-${index}`,
    clientId: item?.clientId ?? item?.id ?? item?._id ?? "",
    clientName: item?.clientName ?? item?.companyName ?? item?.client ?? "",
    aiFadvAccount: item?.aiFadvAccount ?? "",
    aiFadvUser: item?.aiFadvUser ?? "",
    aiFadvPassword: item?.aiFadvPassword ?? "",
    aiFadvSecret: item?.aiFadvSecret ?? "",
    statusId: item?.statusId ?? item?.statusLabel ?? item?.status ?? "",
  }));

const emptyAdvantageForm = {
  account: "",
  user: "",
  password: "",
  secret: "",
  sameAsFirstAdvantage: false,
};

const emptyAdvantageSourceData = {
  fadv: { account: "", user: "", password: "", secret: "" },
  aiFadv: { account: "", user: "", password: "", secret: "" },
};

const hasAdvantageCredentialData = (data = {}) =>
  Boolean(
    String(data?.account || "").trim() ||
      String(data?.user || "").trim() ||
      String(data?.password || "").trim() ||
      String(data?.secret || "").trim()
  );

const AIFadvClientsModal = ({
  isOpen,
  onClose,
  aiFadvUser = "",
  statusId = "",
  statusLabel = "",
}) => {
  const { permissionData } = usePermissionContext() || {};
  const canAdvantageLogin = Boolean(permissionData?.RegisterAIFADVAccess);

  const [loading, setLoading] = useState(false);
  const [clientRows, setClientRows] = useState([]);

  const [advantageLoginModalOpen, setAdvantageLoginModalOpen] = useState(false);
  const [advantageLoginItemId, setAdvantageLoginItemId] = useState("");
  const [advantageLoginClientName, setAdvantageLoginClientName] = useState("");
  const [advantageForm, setAdvantageForm] = useState(emptyAdvantageForm);
  const [advantageSourceData, setAdvantageSourceData] = useState(
    emptyAdvantageSourceData
  );
  const [advantageErrors, setAdvantageErrors] = useState({});
  const [advantageSubmitting, setAdvantageSubmitting] = useState(false);
  const [advantageFetching, setAdvantageFetching] = useState(false);

  const modalTitle = useMemo(() => {
    const userLabel = String(aiFadvUser || "").trim() || "AI FADV User";
    const status = String(statusLabel || statusId || "").trim() || "Clients";
    return `${userLabel} > ${status}`;
  }, [aiFadvUser, statusId, statusLabel]);

  const loadClients = async () => {
    const normalizedStatusId = String(statusId || "").trim();
    if (!normalizedStatusId) {
      setClientRows([]);
      return;
    }

    setLoading(true);
    try {
      const response = await submitAIFadvReportClientsFilterData({
        aiFadvUser: String(aiFadvUser ?? ""),
        statusId: normalizedStatusId,
      });

      if (response == null || response?.success === false) {
        setClientRows([]);
        if (response?.message) {
          toast.error(response.message);
        }
        return;
      }

      setClientRows(mapClientRows(extractClientRows(response)));
    } catch (error) {
      toast.error(error?.message || "Unable to load clients");
      setClientRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setClientRows([]);
      return;
    }

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      try {
        const response = await submitAIFadvReportClientsFilterData({
          aiFadvUser: String(aiFadvUser ?? ""),
          statusId: String(statusId || "").trim(),
        });

        if (cancelled) return;

        if (response == null || response?.success === false) {
          setClientRows([]);
          if (response?.message) {
            toast.error(response.message);
          }
          return;
        }

        setClientRows(mapClientRows(extractClientRows(response)));
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.message || "Unable to load clients");
          setClientRows([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    if (!String(statusId || "").trim()) {
      setClientRows([]);
      return;
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, [isOpen, aiFadvUser, statusId]);

  const closeAdvantageLoginModal = () => {
    setAdvantageLoginModalOpen(false);
    setAdvantageLoginItemId("");
    setAdvantageLoginClientName("");
    setAdvantageForm(emptyAdvantageForm);
    setAdvantageSourceData(emptyAdvantageSourceData);
    setAdvantageErrors({});
    setAdvantageSubmitting(false);
    setAdvantageFetching(false);
  };

  const handleAdvantageLogin = (row = {}) => {
    const targetId = String(row?.clientId ?? row?.id ?? "").trim();
    setAdvantageLoginItemId(targetId);
    setAdvantageLoginClientName(String(row?.clientName ?? "").trim());
    setAdvantageForm(emptyAdvantageForm);
    setAdvantageSourceData(emptyAdvantageSourceData);
    setAdvantageErrors({});
    setAdvantageLoginModalOpen(true);
    if (!targetId) return;

    setAdvantageFetching(true);
    getClientRegisterFadvDetails(targetId)
      .then((response) => {
        const responseData = response?.data || {};
        const nextFadv = {
          account: responseData?.fadv?.account || "",
          user: responseData?.fadv?.user || "",
          password: responseData?.fadv?.password || "",
          secret: responseData?.fadv?.secret || "",
        };
        const nextAiFadv = {
          account: responseData?.aiFadv?.account || "",
          user: responseData?.aiFadv?.user || "",
          password: responseData?.aiFadv?.password || "",
          secret: responseData?.aiFadv?.secret || "",
        };
        const isSameAsFirstAdvantage = Boolean(
          responseData?.sameAsFirstAdvantage || responseData?.isSameAsFirstAdvantage
        );
        if (response) {
          setAdvantageSourceData({
            fadv: nextFadv,
            aiFadv: nextAiFadv,
          });
          const selectedData = isSameAsFirstAdvantage ? nextFadv : nextAiFadv;
          setAdvantageForm((prev) => ({
            ...prev,
            account: selectedData?.account || "",
            user: selectedData?.user || "",
            password: selectedData?.password || "",
            secret: selectedData?.secret || "",
            sameAsFirstAdvantage: isSameAsFirstAdvantage,
          }));
          return;
        }
        toast.error(response?.message || "Unable to fetch First Advantage details");
      })
      .finally(() => setAdvantageFetching(false));
  };

  const handleAdvantageCheckboxChange = (checked) => {
    if (checked) {
      const selectedData = advantageSourceData?.fadv;
      setAdvantageForm((prev) => ({
        ...prev,
        account: selectedData?.account || "",
        user: selectedData?.user || "",
        password: selectedData?.password || "",
        secret: selectedData?.secret || "",
        sameAsFirstAdvantage: true,
      }));
      return;
    }

    const aiFadvData = advantageSourceData?.aiFadv;
    if (hasAdvantageCredentialData(aiFadvData)) {
      setAdvantageForm((prev) => ({
        ...prev,
        account: aiFadvData?.account || "",
        user: aiFadvData?.user || "",
        password: aiFadvData?.password || "",
        secret: aiFadvData?.secret || "",
        sameAsFirstAdvantage: false,
      }));
      return;
    }

    setAdvantageForm((prev) => ({
      ...prev,
      sameAsFirstAdvantage: false,
    }));
  };

  const handleAdvantageSubmit = async (e) => {
    e.preventDefault();

    const nextErrors = {};
    if (!advantageLoginItemId) {
      nextErrors.account = "Unable to identify the selected row";
    }

    if (Object.keys(nextErrors).length) {
      setAdvantageErrors(nextErrors);
      return;
    }

    setAdvantageSubmitting(true);
    const payload = {
      account: String(advantageForm?.account || ""),
      user: String(advantageForm?.user || ""),
      password: String(advantageForm?.password || ""),
      secret: String(advantageForm?.secret || ""),
      sameAsFirstAdvantage: Boolean(advantageForm?.sameAsFirstAdvantage),
    };
    const response = await updateClientRegisterAiFadvDetails(
      advantageLoginItemId,
      payload
    );
    setAdvantageSubmitting(false);

    if (response?.success) {
      toast.success(
        response?.message || "AI First Advantage details saved successfully"
      );
      setClientRows((previous) =>
        previous.map((row) =>
          String(row?.clientId ?? row?.id) === String(advantageLoginItemId)
            ? {
                ...row,
                aiFadvAccount: payload.account,
                aiFadvUser: payload.user,
                aiFadvPassword: payload.password,
                aiFadvSecret: payload.secret,
              }
            : row
        )
      );
      closeAdvantageLoginModal();
      void loadClients();
    } else {
      toast.error(response?.message || "Unable to save AI First Advantage details");
    }
  };

  const closeModal = () => {
    if (loading || advantageLoginModalOpen) return;
    onClose?.();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={closeModal}
        backdrop="static"
        centered
        size="lg"
        className="ai-fadv-clients-modal modal-full-height"
      >
        <ModalBody className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0 text-uppercase">{modalTitle}</h5>
            <Button
              close
              type="button"
              onClick={closeModal}
              disabled={loading || advantageLoginModalOpen}
            />
          </div>

          {loading ? (
            <div className="d-flex justify-content-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <div className="table-responsive">
              <Table bordered className="align-middle mb-0 ai-fadv-clients-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>AI FADV Account</th>
                    <th>Status</th>
                    {canAdvantageLogin ? <th>Action</th> : null}
                  </tr>
                </thead>
                <tbody>
                  {clientRows.length ? (
                    clientRows.map((row, rowIndex) => {
                      const actionTargetId = `ai-fadv-advantage-login-${String(
                        row.id || rowIndex
                      ).replace(/[^a-zA-Z0-9_-]/g, "-")}`;
                      return (
                        <tr key={row.id}>
                          <td>{row.clientName || "—"}</td>
                          <td>{row.aiFadvAccount || "—"}</td>
                          <td>{row.statusId || "—"}</td>
                          {canAdvantageLogin ? (
                            <td>
                              <span
                                className="team-table-row-action-btn d-inline-flex align-items-center justify-content-center cursor-pointer"
                                onClick={() => handleAdvantageLogin(row)}
                                id={actionTargetId}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter" || event.key === " ") {
                                    event.preventDefault();
                                    handleAdvantageLogin(row);
                                  }
                                }}
                              >
                                <i
                                  className="mdi mdi-login font-size-18 text-primary"
                                  aria-hidden="true"
                                />
                              </span>
                              <UncontrolledTooltip
                                target={actionTargetId}
                                placement="top"
                              >
                                Advantage Login
                              </UncontrolledTooltip>
                            </td>
                          ) : null}
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan={canAdvantageLogin ? 4 : 3}
                        className="text-center text-muted py-4"
                      >
                        No clients found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={advantageLoginModalOpen}
        toggle={closeAdvantageLoginModal}
        backdrop="static"
        size="lg"
        centered
        className="modal-full-height"
      >
        <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
          <Form onSubmit={handleAdvantageSubmit}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="fw-bold mb-0">AI FIRST ADVANTAGE LOGIN</h5>
                {advantageLoginClientName ? (
                  <div className="text-muted mt-1">{advantageLoginClientName}</div>
                ) : null}
              </div>
              <Button close type="button" onClick={closeAdvantageLoginModal} />
            </div>

            <hr />
            <Row>
              <Col md={12}>
                <div className="form-check mb-3">
                  <Input
                    id="aiFadvReportSameAsFirstAdvantage"
                    className="form-check-input"
                    type="checkbox"
                    key={`same-as-first-advantage-${advantageLoginItemId}-${Number(
                      !!advantageForm.sameAsFirstAdvantage
                    )}`}
                    defaultChecked={!!advantageForm.sameAsFirstAdvantage}
                    onChange={(e) =>
                      handleAdvantageCheckboxChange(!!e?.target?.checked)
                    }
                  />
                  <Label
                    className="form-check-label"
                    for="aiFadvReportSameAsFirstAdvantage"
                  >
                    Same As First Advantage
                  </Label>
                </div>
              </Col>
            </Row>

            <h4 className="mb-4">AI First Advantage Login Details</h4>

            <Row className="mb-3">
              <Col md={12}>
                <Label>Account number</Label>
                <Input
                  type="text"
                  value={advantageForm.account}
                  onChange={(e) =>
                    setAdvantageForm((prev) => ({
                      ...prev,
                      account: e?.target?.value || "",
                    }))
                  }
                  placeholder="Enter account number"
                  disabled={advantageFetching}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Label>Login ID</Label>
                <Input
                  type="text"
                  value={advantageForm.user}
                  onChange={(e) =>
                    setAdvantageForm((prev) => ({
                      ...prev,
                      user: e?.target?.value || "",
                    }))
                  }
                  placeholder="Enter login ID"
                  disabled={advantageFetching}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Label>Password</Label>
                <Input
                  type="text"
                  value={advantageForm.password}
                  onChange={(e) =>
                    setAdvantageForm((prev) => ({
                      ...prev,
                      password: e?.target?.value || "",
                    }))
                  }
                  placeholder="Enter password"
                  disabled={advantageFetching}
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Label>Secret</Label>
                <Input
                  type="text"
                  value={advantageForm.secret}
                  onChange={(e) =>
                    setAdvantageForm((prev) => ({
                      ...prev,
                      secret: e?.target?.value || "",
                    }))
                  }
                  placeholder="Enter secret"
                  disabled={advantageFetching}
                />
                {advantageErrors?.account ? (
                  <small className="text-danger">{advantageErrors.account}</small>
                ) : null}
              </Col>
            </Row>

            <div className="text-end mt-4">
              <Button color="primary" type="submit" disabled={advantageSubmitting}>
                {advantageSubmitting ? "Saving..." : "Save"}
              </Button>
            </div>
          </Form>
        </ModalBody>
      </Modal>
    </>
  );
};

AIFadvClientsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  aiFadvUser: PropTypes.string,
  statusId: PropTypes.string,
  statusLabel: PropTypes.string,
};

export default AIFadvClientsModal;
